"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useAuthContext } from "@/contexts/AuthContext";
import { useRecordContext } from "@/contexts/RecordContext";
import PracticeReportCard from "@/components/practice/PracticeReportCard";
import { useKatarutaSuggest } from "@/hooks/useKatarutaSuggest";
import KatarutaSuggest from "@/components/KatarutaSuggest";

type PracticeReportDoc = {
  id: string;
  uid: string;
  displayName?: string | null;
  title?: string;
  body?: string;
  tool?: string | null;
  topicText?: string | null;
  imageUrls?: string[];
  createdAt?: any;
  views?: number;
  likesCount?: number;
  isPublic?: boolean;
  visibility?: "private" | "room" | "submit" | "public";
  plannedDate?: string | null;
};

const formatDateTime = (v: any): string => {
  const d =
    typeof v?.toDate === "function"
      ? v.toDate()
      : typeof v?._seconds === "number"
      ? new Date(v._seconds * 1000)
      : v instanceof Date
      ? v
      : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
};

export default function PracticePage() {
  const { clerkUser, isLoading } = useAuthContext();
  const recordContext = useRecordContext();
  const [tab, setTab] = useState<"public" | "mine">("mine");
  const [reports, setReports] = useState<PracticeReportDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // インライン投稿フォーム
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [tempDisplayName, setTempDisplayName] = useState("");

  // textarea 自動伸長（スクロール位置を保持）
  const draftBodyRef = useRef<HTMLTextAreaElement>(null);
  const suggest = useKatarutaSuggest({
    textareaRef: draftBodyRef,
    getText: () => draftBody,
    setText: setDraftBody,
  });
  useEffect(() => {
    const el = draftBodyRef.current;
    if (!el) return;
    const scrollY = window.scrollY;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    window.scrollTo({ top: scrollY, behavior: "instant" });
  }, [draftBody]);

  // ツールバー：選択範囲をマークダウン記法で囲む
  const wrapDraftSelection = (prefix: string, suffix: string) => {
    const el = draftBodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const selected = draftBody.slice(start, end);
    const newBody = draftBody.slice(0, start) + prefix + selected + suffix + draftBody.slice(end);
    setDraftBody(newBody);
    requestAnimationFrame(() => {
      el.focus();
      if (selected) {
        el.setSelectionRange(start + prefix.length, end + prefix.length);
      } else {
        const pos = start + prefix.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const doPost = async () => {
    if (!draftTitle.trim() || !draftBody.trim() || !clerkUser?.id) return;
    setPosting(true);
    setPostError(null);
    const res = await fetch("/api/practice", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: draftTitle.trim(),
        body: draftBody.trim(),
        displayName: recordContext?.userProfile?.displayName ?? "",
      }),
    }).catch(() => null);
    if (!res?.ok) {
      setPostError("保存に失敗しました");
    } else {
      setDraftTitle("");
      setDraftBody("");
      // リストを再取得
      const data = await fetch("/api/practice?mine=true", { cache: "no-store" })
        .then((r) => r.json()).catch(() => []);
      setReports(Array.isArray(data) ? data : []);
    }
    setPosting(false);
  };

  const handlePost = async () => {
    if (!draftTitle.trim() || !draftBody.trim() || !clerkUser?.id) return;
    if (!recordContext?.userProfile?.displayName?.trim()) {
      setShowNameModal(true);
      return;
    }
    await doPost();
  };

  const handleSaveNameAndPost = async () => {
    const name = tempDisplayName.trim();
    if (!name) return;
    await fetch("/api/user-profile", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ displayName: name }),
    });
    await recordContext?.fetchUserProfile?.();
    setShowNameModal(false);
    setTempDisplayName("");
    await doPost();
  };

  useEffect(() => {
    if (isLoading) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const url =
          tab === "mine"
            ? "/api/practice?mine=true"
            : "/api/practice?public=true";
        const res = await fetch(url, { cache: "no-store" });
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          setError(data?.error ?? "取得に失敗しました");
          return;
        }
        setReports(Array.isArray(data) ? data : []);
      } catch {
        setError("通信エラーが発生しました");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [tab, isLoading, clerkUser?.id]);

  return (
    <>
    <main className="min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <header className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold tracking-tight">実践レポート</h1>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link
                href="/people"
                className="text-sm text-gray-400 hover:text-gray-600"
              >
                実践者を見る
              </Link>
              {clerkUser && (
                <Link
                  href="/practice/new"
                  className="text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded px-3 py-1 hover:bg-gray-100 hover:text-gray-900"
                >
                  レポートを書く
                </Link>
              )}
            </div>
          </div>

          <p className="mt-2 text-sm leading-6 text-gray-600">
            カタルタや思考遊具をどのように使ったか、その結果どんなことが起きたかを共有する場所です。
            実践の記録が増えるほど、使い方のヒントも増えていきます。
          </p>
        </header>

        <div className="flex gap-2 mb-6">
          {(["mine", "public"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`text-sm px-3 py-1 rounded-full border ${
                tab === t
                  ? "border-gray-800 bg-gray-800 text-white"
                  : "border-gray-300 text-gray-500 hover:border-gray-500"
              }`}
            >
              {t === "mine" ? "自分の実践" : "みんなの実践"}
            </button>
          ))}
        </div>

        {/* インライン投稿フォーム（自分の実践タブのみ） */}
        {!isLoading && clerkUser && tab === "mine" && (
          <div className="mb-6">
            <input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="タイトル"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400"
            />
            <div className="flex justify-end mt-0.5 mb-1">
              <span className={`text-xs ${draftTitle.length > 180 ? draftTitle.length >= 200 ? "text-red-500 font-medium" : "text-orange-400" : "text-gray-400"}`}>
                {draftTitle.length} / 200
              </span>
            </div>
            {/* ツールバー */}
            <div className="flex gap-1 mb-1 flex-wrap">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("**", "**"); }}
                className="px-2 py-0.5 text-sm font-bold border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="太字"
              >B</button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("*", "*"); }}
                className="px-2 py-0.5 text-sm italic border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="斜体"
              >I</button>
              <span className="w-px bg-gray-200 mx-0.5" />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("### ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="見出し H3"
              >H3</button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("#### ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="見出し H4"
              >H4</button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("- ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="箇条書き"
              >・</button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("1. ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="番号付きリスト"
              >1.</button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); wrapDraftSelection("> ", ""); }}
                className="px-2 py-0.5 text-sm border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                title="引用"
              >"</button>
            </div>
            <div className="mb-2">
              <KatarutaSuggest
                enabled={suggest.enabled}
                setEnabled={suggest.setEnabled}
                suggestMode={suggest.suggestMode}
                setSuggestMode={suggest.setSuggestMode}
                cardType={suggest.cardType}
                setCardType={suggest.setCardType}
                freqMode={suggest.freqMode}
                setFreqMode={suggest.setFreqMode}
                isPaidOrTrial={suggest.isPaidOrTrial}
              />
            </div>
            <div className="relative">
              <textarea
                ref={draftBodyRef}
                value={draftBody}
                onChange={(e) => { setDraftBody(e.target.value); suggest.onTextChange(e.target.value); }}
                onKeyDown={suggest.onKeyDown}
                placeholder="どんな場で、どう使いましたか？　何が起きましたか？"
                rows={3}
                className="w-full border border-gray-200 rounded-md p-3 text-sm focus:outline-none focus:border-gray-400"
                style={{ resize: "none", overflow: "hidden", minHeight: "5rem" }}
              />
              {suggest.suggestion && suggest.coords && (
                <button
                  type="button"
                  onClick={suggest.adopt}
                  style={{ position: "absolute", top: suggest.coords.top, left: suggest.coords.left }}
                  className="text-sm px-3 py-1 rounded-full bg-white/90 border border-gray-200 text-gray-700 shadow-sm hover:bg-gray-50 transition-colors z-10"
                >
                  {suggest.suggestion.word}
                </button>
              )}
            </div>
            <div className="flex items-center justify-end mt-0.5">
              <span className={`text-xs ${draftBody.length > 18000 ? draftBody.length >= 20000 ? "text-red-500 font-medium" : "text-orange-400" : "text-gray-400"}`}>
                {draftBody.length.toLocaleString()} / 20,000
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <button
                onClick={handlePost}
                disabled={posting || !draftTitle.trim() || !draftBody.trim()}
                className="text-sm px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 disabled:opacity-40 hover:bg-gray-50"
              >
                {posting ? "投稿中…" : "投稿する"}
              </button>
              {postError && <span className="text-xs text-red-500">{postError}</span>}
              <Link
                href={`/practice/new${draftTitle || draftBody ? `?title=${encodeURIComponent(draftTitle)}&body=${encodeURIComponent(draftBody)}` : ""}`}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                画像を追加する →
              </Link>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-sm text-gray-500">読み込み中...</div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
            {tab === "mine" ? "まだ実践レポートはありません。" : "公開されている実践レポートはまだありません。"}
          </div>
        )}

        {!loading && !error && reports.length > 0 && (
          <div className="space-y-5">
            {reports.map((report) => (
              <PracticeReportCard
                key={report.id}
                report={{
                  id: report.id,
                  uid: report.uid,
                  displayName: report.displayName ?? null,
                  title: report.title ?? "",
                  body: report.body ?? "",
                  tool: report.tool ?? null,
                  topicText: report.topicText ?? null,
                  imageUrls: report.imageUrls ?? [],
                  views: report.views ?? 0,
                  likesCount: report.likesCount ?? 0,
                  isMine: !!clerkUser?.id && report.uid === clerkUser.id,
                  isPublic: report.isPublic ?? false,
                  visibility: report.visibility ?? "private",
                  plannedDate: report.plannedDate ?? null,
                  createdAtLabel: formatDateTime(report.createdAt),
                }}
                truncate={true}
                showPublishToggle={tab === "mine"}
                onDeleted={() =>
                  setReports((prev) => prev.filter((r) => r.id !== report.id))
                }
              />
            ))}
          </div>
        )}
      </div>
    </main>

    {showNameModal && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
          <h2 className="text-lg font-bold mb-3">
            投稿するには表示名の登録が必要です
          </h2>
          <input
            value={tempDisplayName}
            onChange={(e) => setTempDisplayName(e.target.value)}
            className="w-full border rounded px-3 py-2 mb-4"
            placeholder="表示名"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") handleSaveNameAndPost(); }}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowNameModal(false)}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveNameAndPost}
              disabled={!tempDisplayName.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
            >
              保存して投稿
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
